import { Table, TableCell, TableRow } from '@mui/material'
import cn from 'classnames'
import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ErrorNote from '../common/util/ErrorNote'
import MainButton from '../common/util/input/MainButton'
import { get } from '../helpers/Api'
import { formatNumber } from '../helpers/FormatLinks'
import LoadingIndicator from '../loadingindicator'
import { Context, useSharedState } from '../waxplorer/Waxplorer'

const FtWallet = () => {
    const [state] = useSharedState()

    const activeUser = state?.activeUser

    const userName = activeUser?.accountName
        ? state?.activeUser.accountName
        : null
    const [packs, setPacks] = useState([])
    const [elements, setElements] = useState([])
    const [isLoadingPacks, setIsLoadingPacks] = useState(true)
    const [isLoadingElements, setIsLoadingElements] = useState(true)
    const [errors, setErrors] = useState({})

    const { t } = useTranslation('common')

    const getPacks = async (user) => {
        get('my-packs/' + user).then((result) => {
            setPacks(result)
            setIsLoadingPacks(false)
        })
    }

    const getElements = async (user) => {
        get('my-elements/' + user).then((result) => {
            setElements(result)
            setIsLoadingElements(false)
        })
    }

    const dismissError = (symbol) => {
        delete errors[symbol]
        setErrors(errors)
    }

    const [, dispatch] = useContext(Context)

    const handleSell = (sellInfo) => {
        if (sellInfo) {
            const wasSold = sellInfo['sold']

            if (wasSold) {
                setTimeout(function () {
                    getPacks(userName)
                    getElements(userName)
                }, 2000)
            } else {
                setIsLoadingPacks(false)
                setIsLoadingElements(false)
            }
        }
    }

    const sell = async (pack) => {
        setIsLoadingPacks(true)
        setIsLoadingElements(true)
        dispatch({ type: 'SET_ASSET', payload: pack })
        dispatch({
            type: 'SET_CALLBACK',
            payload: (sellInfo) => handleSell(sellInfo),
        })
        dispatch({ type: 'SET_ACTION', payload: 'sell' })
    }

    useEffect(() => {
        if (userName) Promise.all([getPacks(userName), getElements(userName)])
    }, [userName])

    const tdClassFirst = cn(
        'text-left w-auto overflow-x-auto dark:text-white text-black',
    )
    const tdClassNext = cn(
        'text-center w-auto overflow-x-auto dark:text-white text-black',
    )
    const tableClass = cn('w-full leading-triple')

    return isLoadingPacks || isLoadingElements ? (
        <LoadingIndicator />
    ) : (
        <Table>
            <TableRow className={tableClass}>
                <TableCell></TableCell>
                <TableCell>{t('asset.author')}</TableCell>
                <TableCell>Token</TableCell>
                <TableCell>{t('asset.name')}</TableCell>
                <TableCell>{t('asset.average_sold')}</TableCell>
                <TableCell>{t('asset.last_sold')}</TableCell>
                <TableCell>{t('search.market')}</TableCell>
                <TableCell>{t('asset.owned')}</TableCell>
                <TableCell></TableCell>
            </TableRow>
            {packs
                ? packs.map((pack) => {
                      const sellButton = (
                          <MainButton
                              className="relative top-0 left-0"
                              onClick={() => sell(pack)}
                          >
                              {t('asset.sell')} 1
                          </MainButton>
                      )
                      return (
                          <TableRow
                              key={
                                  'key_' + pack['image'] + '_' + pack['symbol']
                              }
                          >
                              <TableCell>
                                  <div>
                                      <img
                                          className="max-h-24 m-auto"
                                          src={pack['image']}
                                          alt={pack['symbol']}
                                      />
                                  </div>
                              </TableCell>
                              <TableCell>{pack['author']}</TableCell>
                              <TableCell>{pack['symbol']}</TableCell>
                              <TableCell>{pack['name']}</TableCell>
                              <TableCell>
                                  {pack['average']
                                      ? `${formatNumber(
                                            pack['average'],
                                        )} WAX / $${formatNumber(
                                            pack['usd_average'],
                                        )}`
                                      : ''}
                              </TableCell>
                              <TableCell>
                                  {pack['last_sold']
                                      ? `${formatNumber(
                                            pack['last_sold'],
                                        )} WAX / $${formatNumber(
                                            pack['usd_last_sold'],
                                        )}`
                                      : ''}
                              </TableCell>
                              <TableCell>
                                  {pack['lowest']
                                      ? `${formatNumber(
                                            pack['lowest'],
                                        )} WAX / $${formatNumber(
                                            pack['usd_lowest'],
                                        )}`
                                      : ''}
                              </TableCell>
                              <TableCell>{pack['amount']}</TableCell>
                              <TableCell>
                                  {pack['amount'] > 0 ? sellButton : ''}
                                  {errors['symbol'] ? (
                                      <ErrorNote
                                          onClick={() =>
                                              dismissError(pack['symbol'])
                                          }
                                          error={errors['symbol']}
                                      />
                                  ) : (
                                      ''
                                  )}
                              </TableCell>
                          </TableRow>
                      )
                  })
                : ''}
            {elements
                ? elements.map((element) => {
                      const sellButton = (
                          <MainButton
                              className="relative top-0 left-0"
                              onClick={() => sell(element)}
                          >
                              {t('asset.sell')} 1
                          </MainButton>
                      )
                      return (
                          <TableRow
                              key={
                                  'key_' +
                                  element['image'] +
                                  '_' +
                                  element['symbol']
                              }
                          >
                              <TableCell>
                                  <div>
                                      <img
                                          className="max-h-24 m-auto"
                                          src={element['image']}
                                          alt={element['symbol']}
                                      />
                                  </div>
                              </TableCell>
                              <TableCell>{element['author']}</TableCell>
                              <TableCell>{element['symbol']}</TableCell>
                              <TableCell>{element['name']}</TableCell>
                              <TableCell>
                                  {element['average']
                                      ? `${formatNumber(
                                            element['average'],
                                        )} WAX / $${formatNumber(
                                            element['usd_average'],
                                        )}`
                                      : ''}
                              </TableCell>
                              <TableCell>
                                  {element['last_sold']
                                      ? `${formatNumber(
                                            element['last_sold'],
                                        )} WAX / $${formatNumber(
                                            element['usd_last_sold'],
                                        )}`
                                      : ''}
                              </TableCell>
                              <TableCell>
                                  {element['lowest']
                                      ? `${formatNumber(
                                            element['lowest'],
                                        )} WAX / $${formatNumber(
                                            element['usd_lowest'],
                                        )}`
                                      : ''}
                              </TableCell>
                              <TableCell>{element['amount']}</TableCell>
                              <TableCell>
                                  {element['amount'] > 0 ? sellButton : ''}
                                  {errors['symbol'] ? (
                                      <ErrorNote
                                          onClick={() =>
                                              dismissError(element['symbol'])
                                          }
                                          error={errors['symbol']}
                                      />
                                  ) : (
                                      ''
                                  )}
                              </TableCell>
                          </TableRow>
                      )
                  })
                : ''}
        </Table>
    )
}

export default FtWallet
